<template>
  <div class="admin-panel task-page task-page--tax">

    <admin-header
      variant="back"
      :crumbs="[{ label: 'Client Management', to: { name: 'Admin-Panel', params: { lang: $route.params.lang } } }, { label: 'Tax Tasks' }]"
      :back-to="{ name: 'Admin-Panel', params: { lang: $route.params.lang } }"
      @open-logout="openLogoutModal"
    />

    <main class="ap-container ap-fade-in">

      <!-- Page hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'file-invoice']" />
        </div>
        <div>
          <h1 class="tp-hero-title">Tax Tasks</h1>
          <p class="tp-hero-sub">Track and manage all tax-related deadlines and submissions.</p>
        </div>
      </div>

      <!-- Ad hoc notification strip -->
      <div class="tp-adhoc-strip" :class="{ 'is-open': adhocStripOpen, 'has-items': adhocStripItems.length }">
        <div class="tp-adhoc-strip-toggle" @click="adhocStripOpen = !adhocStripOpen">
          <span class="tp-adhoc-strip-label">
            <fa-icon :icon="['fas', 'calendar-times']" class="tp-adhoc-strip-icon" />
            <template v-if="viewMode === 'calendar'">
              Ad Hoc Deadlines — <strong>{{ adhocStripItems.length }}</strong> in {{ monthNames[calendarMonth - 1] }}
            </template>
            <template v-else>
              Ad Hoc Deadlines — <strong>{{ adhocStripItems.length }}</strong> total this year
            </template>
            <span> | </span>
            <app-button  @click.stop="showAdHocForm = true" variant="custom" customBg="#d97706" customHoverBg="#B16105" size="xs" :icon="['fas', 'plus']">
              Add
            </app-button>
          </span>
          <div class="tp-adhoc-strip-actions">
            <fa-icon :icon="['fas', adhocStripOpen ? 'chevron-up' : 'chevron-down']" class="tp-adhoc-strip-caret" />
          </div>
        </div>
        <transition name="tp-adhoc-collapse">
          <div v-if="adhocStripOpen" class="tp-adhoc-strip-body">
            <!-- Calendar view: flat list for the viewed month -->
            <template v-if="viewMode === 'calendar'">
              <div v-if="!adhocStripItems.length" class="tp-adhoc-strip-empty">
                No ad hoc deadlines in {{ monthNames[calendarMonth - 1] }}.
              </div>
              <div v-else class="tp-adhoc-strip-list">
                <div v-for="item in adhocStripItems" :key="item.adhocId" class="tp-adhoc-strip-item">
                  <span class="tp-adhoc-strip-item-chip">
                    <span class="tp-adhoc-strip-item-initial" :title="item.clientName">{{ item.initial }}</span>
                    <span class="tp-adhoc-strip-item-task">{{ item.display_name }}</span>
                    <span class="tp-adhoc-strip-item-day">{{ item.date.day }}/{{ item.date.month }}</span>
                    <app-divider color="#d1d5db" height="20px"></app-divider>
                  </span>
                  <button class="tp-adhoc-strip-delete" title="Delete" @click="confirmDeleteAdhoc(item)">
                    <fa-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </template>
            <!-- Table view: all months grouped -->
            <template v-else>
              <div v-if="!adhocStripGroups.length" class="tp-adhoc-strip-empty">
                No ad hoc deadlines this year.
              </div>
              <template v-else>
                <div v-for="group in adhocStripGroups" :key="group.month" class="tp-adhoc-strip-group">
                  <div class="tp-adhoc-strip-group-month">{{ group.monthName }}</div>
                  <div class="tp-adhoc-strip-list">
                    <div v-for="item in group.items" :key="item.adhocId" class="tp-adhoc-strip-item">
                      <span class="tp-adhoc-strip-item-chip">
                        <span class="tp-adhoc-strip-item-initial" :title="item.clientName">{{ item.initial }}</span>
                        <span class="tp-adhoc-strip-item-task">{{ item.display_name }}</span>
                        <span class="tp-adhoc-strip-item-day">{{ item.date.day }}/{{ item.date.month }}</span>
                        <app-divider color="#b8860b" height="12px"></app-divider>
                        <app-tooltip text="Delete" position="top" truncate>
                        <button class="tp-adhoc-strip-delete" @click="confirmDeleteAdhoc(item)">
                          <fa-icon :icon="['fas', 'trash']" />
                        </button>
                        </app-tooltip>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </transition>
      </div>

      <!-- Admin dates strip -->
      <div class="tp-adhoc-strip tp-admin-date-strip" :class="{ 'is-open': adminDateStripOpen, 'has-items': adminDateStripItems.length }">
        <div class="tp-adhoc-strip-toggle" @click="adminDateStripOpen = !adminDateStripOpen">
          <span class="tp-adhoc-strip-label">
            <fa-icon :icon="['fas', 'calendar-check']" class="tp-adhoc-strip-icon" />
            <template v-if="viewMode === 'calendar'">
              Admin Dates — <strong>{{ adminDateStripItems.length }}</strong> in {{ monthNames[calendarMonth - 1] }}
            </template>
            <template v-else>
              Admin Dates — <strong>{{ adminDateStripItems.length }}</strong> total this year
            </template>
            <span> | </span>
            <app-button @click.stop="showAdminDateForm = true" variant="custom" customBg="#1d4ed8" customHoverBg="#1e40af" size="xs" :icon="['fas', 'plus']">
              Add
            </app-button>
          </span>
          <div class="tp-adhoc-strip-actions">
            <fa-icon :icon="['fas', adminDateStripOpen ? 'chevron-up' : 'chevron-down']" class="tp-adhoc-strip-caret" />
          </div>
        </div>
        <transition name="tp-adhoc-collapse">
          <div v-if="adminDateStripOpen" class="tp-adhoc-strip-body">
            <template v-if="viewMode === 'calendar'">
              <div v-if="!adminDateStripItems.length" class="tp-adhoc-strip-empty">No admin dates in {{ monthNames[calendarMonth - 1] }}.</div>
              <div v-else class="tp-adhoc-strip-list">
                <div v-for="ad in adminDateStripItems" :key="ad.id" class="tp-adhoc-strip-item">
                  <span class="tp-adhoc-strip-item-chip tp-ad-chip">
                    <span class="tp-adhoc-strip-item-task">{{ ad.title }}</span>
                    <span class="tp-adhoc-strip-item-day">{{ ad.date.day }}/{{ ad.date.month }}</span>
                    <app-divider color="#d1d5db" height="20px"></app-divider>
                  </span>
                  <button class="tp-adhoc-strip-delete" title="Delete" @click="confirmDeleteAdminDate(ad)">
                    <fa-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-if="!adminDateStripGroups.length" class="tp-adhoc-strip-empty">No admin dates this year.</div>
              <template v-else>
                <div v-for="group in adminDateStripGroups" :key="group.month" class="tp-adhoc-strip-group">
                  <div class="tp-adhoc-strip-group-month">{{ group.monthName }}</div>
                  <div class="tp-adhoc-strip-list">
                    <div v-for="ad in group.items" :key="ad.id" class="tp-adhoc-strip-item">
                      <span class="tp-adhoc-strip-item-chip tp-ad-chip">
                        <span class="tp-adhoc-strip-item-task">{{ ad.title }}</span>
                        <span class="tp-adhoc-strip-item-day">{{ ad.date.day }}/{{ ad.date.month }}</span>
                        <app-divider color="#1d4ed8" height="12px"></app-divider>
                        <app-tooltip text="Delete" position="top" truncate>
                          <button class="tp-adhoc-strip-delete" @click="confirmDeleteAdminDate(ad)">
                            <fa-icon :icon="['fas', 'trash']" />
                          </button>
                        </app-tooltip>
                      </span>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
        </transition>
      </div>

      <!-- Summary strip -->
      <div class="tp-summary">
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ taxClients.length }}</span>
          <span class="tp-summary-label">Tax Clients</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ taxFilingDueThisMonth }}</span>
          <span class="tp-summary-label">Tax Filing</span>
          <span class="tp-summary-label">Due This Month</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ mitrsDueThisMonth }}</span>
          <span class="tp-summary-label">MITRS </span>
          <span class="tp-summary-label">Due This Month</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ cp204DueThisMonth }}</span>
          <span class="tp-summary-label">CP204 / CP204A</span>
          <span class="tp-summary-label">Due This Month</span>
        </div>
        <div class="tp-summary-card">
          <span class="tp-summary-val">{{ tpDocDueThisMonth }}</span>
          <span class="tp-summary-label">TP Doc Reminder</span>
          <span class="tp-summary-label">Due This Month</span>
        </div>
        <div class="tp-summary-card" :class="{ 'tp-summary-card--alert': deadlinesMissed > 0 }">
          <span class="tp-summary-val">{{ deadlinesMissed }}</span>
          <span class="tp-summary-label">Deadline Missed</span>
        </div>
      </div>

      <!-- Calendar Table Panel -->
      <div class="tp-panel">
        <div class="tp-panel-head">
          <h3>Tax Calendar</h3>
          <div class="tp-panel-head-right">
            <!-- Year slot tabs -->
            <div ref="yearTabs" class="tp-year-tabs">
              <span class="tp-year-pill" :style="pillStyle" />
              <button
                v-for="opt in slotOptions"
                :key="opt.slot"
                class="tp-year-tab"
                :class="{ 'is-active': selectedSlot === opt.slot }"
                @click="selectedSlot = opt.slot"
              >{{ opt.year }}</button>
            </div>
             <app-divider color="#d1d5db" height="20px"></app-divider>
            <!-- View-mode toggle: table / calendar -->
            <div class="tp-view-toggle">
              <button
                class="tp-view-toggle-btn"
                :class="{ 'is-active': viewMode === 'table' }"
                title="Table View"
                @click="viewMode = 'table'"
              >
                <fa-icon :icon="['fas', 'table']" />
              </button>
              <button
                class="tp-view-toggle-btn"
                :class="{ 'is-active': viewMode === 'calendar' }"
                title="Calendar View"
                @click="viewMode = 'calendar'"
              >
                <fa-icon :icon="['fas', 'calendar-alt']" />
              </button>
            </div>
            <!-- Year rollover -->
            <div v-if="rolloverNeeded">
              <app-divider color="#d1d5db" height="20px"></app-divider>
              <app-button size="sm" variant="danger" :bold="true" @click="showRolloverConfirm = true">
                <fa-icon :icon="['fas', 'undo-alt']" />
                Year Rollover
              </app-button>
            </div>
            <!-- Total clients -->
            <app-divider color="#d1d5db" height="20px"></app-divider>
            <app-badge v-if="!loading" size="xl" :caps="false" variant="custom" custom-color="#374151" custom-bg="e5e7eb" :inner-shadow="true">
              <span>{{ taxClients.length }} client{{ taxClients.length !== 1 ? 's' : '' }}</span></app-badge>
          </div>
        </div>

        <!-- Column visibility toolbar — table mode only -->
        <div v-if="viewMode === 'table' && !loading && taxClients.length" class="tp-col-toolbar">
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--tax"
            :class="{ active: !hiddenMetaCols.includes('initial') }"
            :title="(hiddenMetaCols.includes('initial') ? 'Show' : 'Hide') + ' Initial column'"
            @click="toggleMetaCol('initial')"
          >Initial</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--tax"
            :class="{ active: !hiddenMetaCols.includes('type') }"
            :title="(hiddenMetaCols.includes('type') ? 'Show' : 'Hide') + ' Type column'"
            @click="toggleMetaCol('type')"
          >Type</button>
          <button
            class="ap-filter-chip ap-filter-chip-cat ap-filter-chip-cat--tax"
            :class="{ active: !hiddenMetaCols.includes('year_end') }"
            :title="(hiddenMetaCols.includes('year_end') ? 'Show' : 'Hide') + ' Year End column'"
            @click="toggleMetaCol('year_end')"
          >Year End</button>
        </div>

        <div v-if="loading" class="tp-empty">
          <fa-icon :icon="['fas', 'spinner']" spin class="tp-empty-icon" />
          <p>Loading tax clients…</p>
        </div>

        <div v-else-if="!taxClients.length" class="tp-empty">
          <fa-icon :icon="['fas', 'file-invoice']" class="tp-empty-icon" />
          <p>No tax clients found. Add clients with the Tax service to see their deadlines here.</p>
        </div>

        <!-- Calendar view -->
        <task-calendar-view
          v-else-if="viewMode === 'calendar'"
          :clients="taxClients"
          :calendar-month="calendarMonth"
          :calendar-year="calendarYear"
          :current-month="currentMonth"
          :current-year="currentYear"
          :selected-slot="selectedSlot"
          :month-names="monthNames"
          :updating-task="updatingTask"
          :updating-adhoc="updatingAdhoc"
          :admin-dates="adminDates"
          :updating-admin-date="updatingAdminDate"
          :get-deadlines="getDeadlines"
          :deadline-class="deadlineClass"
          @update-status="updateTaskStatus"
          @toggle-admin-date="updateAdminDateStatus"
          @prev-month="prevCalMonth"
          @next-month="nextCalMonth"
          @set-month="setCalMonth"
        />

        <!-- Table view -->
        <div v-else ref="calWrap" class="tp-cal-wrap" :style="{ height: tableHeight }">
          <table class="tp-cal-table">
            <colgroup>
              <col class="tp-col-num">
              <col class="tp-col-company">
              <col v-if="!hiddenMetaCols.includes('initial')" class="tp-col-initial">
              <col v-if="!hiddenMetaCols.includes('type')" class="tp-col-meta">
              <col v-if="!hiddenMetaCols.includes('year_end')" class="tp-col-meta">
              <col v-for="m in 12" :key="m" class="tp-col-month">
              <col class="tp-col-actions">
              <col class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }">
            </colgroup>
            <thead>
              <tr>
                <th class="tp-cal-th tp-cal-sticky tp-cal-th-num" style="left:0">#</th>
                <th class="tp-cal-th tp-cal-sticky tp-cal-th-company" style="left:50px">Client</th>
                <th v-if="!hiddenMetaCols.includes('initial')" class="tp-cal-th tp-cal-th-meta">Initial</th>
                <th v-if="!hiddenMetaCols.includes('type')" class="tp-cal-th tp-cal-th-meta">Type</th>
                <th v-if="!hiddenMetaCols.includes('year_end')" class="tp-cal-th tp-cal-th-meta">Year End</th>
                <th
                  v-for="(name, i) in monthNames"
                  :key="i"
                  class="tp-cal-th tp-cal-th-month"
                  :class="{ 'tp-cal-th-current': i + 1 === currentMonth && selectedSlot === 'current' }"
                >{{ name }}</th>
                <th class="tp-cal-th"></th>
                <th class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(client, i) in taxClients" :key="client.id" class="tp-cal-row">
                <td class="tp-cal-td tp-cal-sticky tp-cal-td-num" style="left:0">{{ i + 1 }}</td>
                <td class="tp-cal-td tp-cal-sticky tp-cal-td-company" style="left:50px">{{ client.client }}</td>
                <td v-if="!hiddenMetaCols.includes('initial')" class="tp-cal-td tp-cal-td-meta">{{ client.initial || '—' }}</td>
                <td v-if="!hiddenMetaCols.includes('type')" class="tp-cal-td tp-cal-td-meta">{{ formatClientType(client.client_type) }}</td>
                <td v-if="!hiddenMetaCols.includes('year_end')" class="tp-cal-td tp-cal-td-meta" :class="{ 'ap-cell-empty': !client.year_end }">{{ formatYearEnd(client.year_end) }}</td>
                <td
                  v-for="m in 12"
                  :key="m"
                  class="tp-cal-td tp-cal-td-month"
                  :class="{ 'tp-cal-td-current': m === currentMonth && selectedSlot === 'current' }"
                >
                  <div
                    v-for="dl in getDeadlines(client, m)"
                    :key="dl.is_adhoc ? 'adhoc-' + dl.id : dl.name"
                    class="tp-cal-deadline"
                    :class="deadlineClass(dl, m)"
                  >
                    <!-- <span v-if="!isMonthEnd(m, dl.date.day)" class="tp-cal-dl-day">{{ dl.date.day }}</span> -->
                    <span class="tp-cal-dl-day">{{ dl.date.day }}</span>
                    <span class="tp-cal-dl-label">{{ dl.display_name }}</span>
                    <span v-if="showStatus(dl, m)" class="tp-cal-dl-status" :class="{ 'tp-cal-dl-status--overdue': isOverduePending(dl, m), 'tp-cal-dl-status--completed': dl.status === 'completed' }">
                      {{ dl.status === 'completed' ? 'Completed' : 'Pending' }}
                    </span>
                    <fa-icon
                      v-if="dl.is_adhoc ? updatingAdhoc[dl.id] : updatingTask[client.id + '-' + dl.name]"
                      :icon="['fas', 'spinner']"
                      spin
                      class="tp-dl-status-spinner"
                    />
                    <div
                      v-else
                      class="tp-dl-overlay-trigger"
                      @click.stop="openDlDropdown(client, dl, $event)"
                    ></div>
                  </div>
                </td>
                <td class="tp-cal-td tp-cal-td-actions">
                  <div class="ap-row-actions">
                    <app-tooltip text="view" position="right">
                      <button class="ap-row-action ap-row-action-view" title="View" @click="viewingClient = client; showViewModal = true">
                        <fa-icon :icon="['fas', 'eye']" />
                      </button>
                    </app-tooltip>
                    <app-tooltip text="edit" position="right">
                      <button class="ap-row-action ap-row-action-edit" title="Edit" @click="$router.push({ name: 'Admin-Edit-Client', params: { lang: $route.params.lang, id: client.id } })">
                        <fa-icon :icon="['fas', 'edit']" />
                      </button>
                    </app-tooltip>
                  </div>
                </td>
                <td class="tp-col-spacer" :style="{ minWidth: spacerWidth + 'px' }"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </main>

    <client-details-modal
      :show="showViewModal"
      :client="viewingClient"
      :show-documents="false"
      @close="showViewModal = false"
    />

    <!-- Admin date form -->
    <admin-date-form
      :show="showAdminDateForm"
      context="tax"
      :year="calendarYear"
      @close="showAdminDateForm = false"
      @saved="onAdminDateSaved"
    />

    <!-- Delete admin date confirmation -->
    <confirm-modal
      :show="adminDateDeleteModal.show"
      title="Delete Admin Date"
      confirm-label="Delete"
      cancel-label="Cancel"
      loading-label="Deleting…"
      :icon="['fas', 'trash']"
      :danger="true"
      :loading="adminDateDeleteModal.deleting"
      @cancel="adminDateDeleteModal.show = false"
      @confirm="deleteAdminDate"
    >
      Remove "<strong>{{ adminDateDeleteModal.item && adminDateDeleteModal.item.title }}</strong>"? This cannot be undone.
    </confirm-modal>

    <!-- Ad hoc deadline form -->
    <ad-hoc-deadline-form
      :show="showAdHocForm"
      :clients="taxClients"
      context="tax"
      @close="showAdHocForm = false"
      @saved="onAdHocSaved"
    />

    <!-- Delete ad hoc confirmation -->
    <confirm-modal
      :show="adhocDeleteModal.show"
      title="Delete Ad Hoc Deadline"
      confirm-label="Delete"
      cancel-label="Cancel"
      loading-label="Deleting…"
      :icon="['fas', 'trash']"
      :danger="true"
      :loading="adhocDeleteModal.deleting"
      @cancel="adhocDeleteModal.show = false"
      @confirm="deleteAdhoc"
    >
      Remove "<strong>{{ adhocDeleteModal.item && adhocDeleteModal.item.display_name }}</strong>"
      from <strong>{{ adhocDeleteModal.item && adhocDeleteModal.item.clientName }}</strong>? This cannot be undone.
    </confirm-modal>

    <confirm-modal
      :show="showRolloverConfirm"
      title="Run Year Rollover?"
      confirm-label="Run Rollover"
      cancel-label="Cancel"
      loading-label="Rolling over…"
      :icon="['fas', 'undo-alt']"
      :danger="true"
      :loading="migrating"
      @cancel="showRolloverConfirm = false"
      @confirm="showRolloverConfirm = false; migrateYear(true)"
    >
      This will archive current year records, promote next year to current, and reset pending statuses.
      <strong>This cannot be undone.</strong>
    </confirm-modal>

    <confirm-modal
      :show="showRolloverReminder"
      title="Year Rollover Detected"
      confirm-label="Run Rollover"
      cancel-label="Dismiss"
      loading-label="Rolling over…"
      :icon="['fas', 'sync-alt']"
      :danger="false"
      :loading="migrating"
      @cancel="showRolloverReminder = false"
      @confirm="migrateYear(true)"
    >
      Today is <strong>{{ serverDateFormatted }}</strong> — a new tax year has been detected.
      Running the rollover will archive {{ serverYear - 1 }} records, promote next year to current, and reset pending statuses.
      Click <em>Dismiss</em> to be reminded again on your next visit.
    </confirm-modal>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

    <!--
      Portalled deadline status dropdown — rendered here (Vue reactivity works),
      moved to <body> in mounted() so position:fixed is always viewport-relative.
      One shared instance repositioned per chip click.
    -->
    <div
      ref="tpDlMenu"
      class="ap-si-menu tp-dl-dropdown-menu"
      v-show="!!openDropdownKey"
      :style="dropdownStyle"
      @click.stop
    >
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && (openDropdownDl.status !== 'completed') }"
        @click="selectDlStatus('pending')"
      >
        <span class="ap-si-opt-label">Pending</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status !== 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
      <div
        class="ap-si-opt"
        :class="{ 'is-active': openDropdownDl && openDropdownDl.status === 'completed' }"
        @click="selectDlStatus('completed')"
      >
        <span class="ap-si-opt-label">Completed</span>
        <fa-icon
          v-if="openDropdownDl && openDropdownDl.status === 'completed'"
          :icon="['fas', 'check']"
          class="ap-si-opt-check"
        />
      </div>
    </div>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import ClientDetailsModal from '@/components/shared/ClientDetailsModal.vue'
import TaskCalendarView from '@/components/shared/TaskCalendarView.vue'
import AdHocDeadlineForm from '@/components/shared/AdHocDeadlineForm.vue'
import AdminDateForm from '@/components/shared/AdminDateForm.vue'
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'
import AppButton from '../../components/shared/AppButton.vue'
import AppBadge from '../../components/shared/AppBadge.vue'
import AppDivider from '../../components/shared/AppDivider.vue'
import AppTooltip from '@/components/shared/AppTooltip.vue'

let _rolloverChecked = false
let _rolloverNeeded = false

export default {
  name: 'admin-task-tax',
  mixins: [adminSessionMixin],
  components: { AdminHeader, SessionModal, ConfirmModal, ClientDetailsModal, TaskCalendarView, AdHocDeadlineForm, AdminDateForm, AppButton, AppBadge, AppDivider, AppTooltip },
  data () {
    const now = new Date()
    return {
      taxCache: { prev: null, current: null, next: null },
      loading: false,
      migrating: false,
      showRolloverReminder: false,
      showRolloverConfirm: false,
      rolloverNeeded: false,
      viewingClient: null,
      showViewModal: false,
      serverYear: null,
      serverDateFormatted: '',
      updatingTask: {},
      updatingAdhoc: {},
      selectedSlot: 'current',
      viewMode: 'table',
      calendarMonth: now.getMonth() + 1,
      monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      currentMonth: now.getMonth() + 1,
      currentYear: now.getFullYear(),
      pillStyle: {},
      tableHeight: 'auto',
      spacerWidth: 0,
      hiddenMetaCols: [],
      showAdHocForm: false,
      adhocStripOpen: false,
      adhocDeleteModal: { show: false, deleting: false, item: null },
      adminDates: [],
      showAdminDateForm: false,
      adminDateStripOpen: false,
      adminDateDeleteModal: { show: false, deleting: false, item: null },
      updatingAdminDate: {},
      // Deadline status custom dropdown
      openDropdownKey: null,
      openDropdownClient: null,
      openDropdownDl: null,
      dropdownStyle: {}
    }
  },
  computed: {
    taxClients () {
      return this.taxCache[this.selectedSlot] || []
    },
    calendarYear () {
      if (this.selectedSlot === 'prev') return this.currentYear - 1
      if (this.selectedSlot === 'next') return this.currentYear + 1
      return this.currentYear
    },
    slotOptions () {
      const y = this.currentYear
      return [
        { slot: 'prev', year: y - 1 },
        { slot: 'current', year: y },
        { slot: 'next', year: y + 1 }
      ]
    },
    totalDeadlines () {
      return this.taxClients.reduce((sum, c) => sum + (c.tax ? c.tax.length : 0), 0)
    },
    currentMonthDeadlines () {
      return this.taxClients.reduce((sum, c) => sum + this.getDeadlines(c, this.currentMonth).length, 0)
    },
    nextMonthDeadlines () {
      const next = this.currentMonth === 12 ? 1 : this.currentMonth + 1
      return this.taxClients.reduce((sum, c) => sum + this.getDeadlines(c, next).length, 0)
    },
    taxFilingDueThisMonth () {
      return this.taxClients.reduce((sum, c) => sum + (c.tax || []).filter(item =>
        item.date && item.date.month === this.currentMonth &&
        item.name === 'tax_filing' &&
        item.status !== 'completed'
      ).length, 0)
    },
    mitrsDueThisMonth () {
      return this.taxClients.reduce((sum, c) => sum + (c.tax || []).filter(item =>
        item.date && item.date.month === this.currentMonth &&
        item.name === 'mitrs' &&
        item.status !== 'completed'
      ).length, 0)
    },
    cp204DueThisMonth () {
      return this.taxClients.reduce((sum, c) => sum + (c.tax || []).filter(item =>
        item.date && item.date.month === this.currentMonth &&
        (item.name === 'cp204' || item.name.startsWith('cp204a')) &&
        item.status !== 'completed'
      ).length, 0)
    },
    tpDocDueThisMonth () {
      return this.taxClients.reduce((sum, c) => sum + (c.tax || []).filter(item =>
        item.date && item.date.month === this.currentMonth &&
        (item.name === 'tp_reminder' || item.name === 'transfer_pricing') &&
        item.status !== 'completed'
      ).length, 0)
    },
    deadlinesMissed () {
      if (this.selectedSlot === 'next') return 0
      return this.taxClients.reduce((sum, c) => sum + (c.tax || []).filter(item => {
        if (!item.date || item.status === 'completed') return false
        if (this.selectedSlot === 'prev') return true
        return item.date.month < this.currentMonth
      }).length, 0)
    },
    adhocStripItems () {
      const enrich = (item, client) => ({ ...item, adhocId: item.id, clientId: client.id, clientName: client.client, initial: client.initial || client.client.slice(0, 4) })
      if (this.viewMode === 'calendar') {
        const m = this.calendarMonth
        const items = []
        for (const client of this.taxClients) {
          for (const item of (client.adhoc || [])) {
            if (item.date && item.date.month === m) items.push(enrich(item, client))
          }
        }
        return items
      }
      const items = []
      for (const client of this.taxClients) {
        for (const item of (client.adhoc || [])) items.push(enrich(item, client))
      }
      return items
    },
    adhocStripGroups () {
      const map = {}
      for (const item of this.adhocStripItems) {
        const m = item.date && item.date.month
        if (!m) continue
        if (!map[m]) map[m] = []
        map[m].push(item)
      }
      return Object.keys(map).map(Number).sort((a, b) => a - b)
        .map(m => ({ month: m, monthName: this.monthNames[m - 1], items: map[m] }))
    },
    adminDateStripItems () {
      if (this.viewMode === 'calendar') {
        return this.adminDates.filter(ad => ad.date && ad.date.month === this.calendarMonth)
      }
      return this.adminDates
    },
    adminDateStripGroups () {
      const map = {}
      for (const ad of this.adminDates) {
        const m = ad.date && ad.date.month
        if (!m) continue
        if (!map[m]) map[m] = []
        map[m].push(ad)
      }
      return Object.keys(map).map(Number).sort((a, b) => a - b)
        .map(m => ({ month: m, monthName: this.monthNames[m - 1], items: map[m] }))
    }
  },
  watch: {
    selectedSlot (slot) {
      if (this.taxCache[slot] === null) this.fetchSlot(slot)
      this.fetchAdminDates()
      this.$nextTick(this.updatePill)
    },
    viewMode (val) {
      if (val === 'table') this.$nextTick(this.computeTableHeight)
    },
    showRolloverReminder () {
      this.$nextTick(this.computeTableHeight)
    },
    showAdHocForm (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    }
  },
  mounted () {
    this.startSessionTimer()
    this.loadColSettings()
    this.fetchSlot('current')
    this.fetchAdminDates()
    this.checkRolloverReminder()
    window.addEventListener('resize', this.computeTableHeight)
    window.addEventListener('resize', this.updatePill)
    window.addEventListener('resize', this.computeSpacerWidth)
    this.$nextTick(this.updatePill)
    this.$nextTick(this.computeSpacerWidth)
    // Portal: move deadline dropdown to <body> so position:fixed is viewport-relative
    document.body.appendChild(this.$refs.tpDlMenu)
    document.addEventListener('click', this.onDlOutsideClick)
    window.addEventListener('scroll', this.onDlScroll, true)
    window.addEventListener('resize', this.closeDlDropdown)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.computeTableHeight)
    window.removeEventListener('resize', this.updatePill)
    window.removeEventListener('resize', this.computeSpacerWidth)
    window.removeEventListener('resize', this.closeDlDropdown)
    if (this.showAdHocForm) unlockBodyScroll()
    document.removeEventListener('click', this.onDlOutsideClick)
    window.removeEventListener('scroll', this.onDlScroll, true)
    const menu = this.$refs.tpDlMenu
    if (menu && menu.parentNode === document.body) {
      document.body.removeChild(menu)
    }
  },
  methods: {
    // ── Column visibility persistence ─────────────────────────────────────────
    async loadColSettings () {
      try {
        const data = await this.adminFetch('/api/admin/settings/get.php')
        if (data && data.success && Array.isArray(data.data.tax_col_visibility)) {
          this.hiddenMetaCols = data.data.tax_col_visibility
        }
      } catch (err) { /* silent — keep defaults */ }
    },
    saveColSettings () {
      this.adminFetch('/api/admin/settings/save.php', {
        method: 'POST',
        body: JSON.stringify({ tax_col_visibility: this.hiddenMetaCols })
      }).catch(() => {})
    },
    // ─────────────────────────────────────────────────────────────────────────
    updatePill () {
      const container = this.$refs.yearTabs
      if (!container) return
      const active = container.querySelector('.tp-year-tab.is-active')
      if (!active) return
      const cr = container.getBoundingClientRect()
      const ar = active.getBoundingClientRect()
      this.pillStyle = { left: `${ar.left - cr.left}px`, width: `${ar.width}px` }
    },
    prevCalMonth () { if (this.calendarMonth > 1) this.calendarMonth-- },
    nextCalMonth () { if (this.calendarMonth < 12) this.calendarMonth++ },
    setCalMonth (m) { this.calendarMonth = m },
    async checkRolloverReminder () {
      if (_rolloverChecked) {
        this.showRolloverReminder = _rolloverNeeded
        return
      }
      try {
        const data = await this.adminFetch('/api/admin/tasks/server-date.php')
        if (data && data.success) {
          _rolloverChecked = true
          _rolloverNeeded = data.need_rollover === true
          this.rolloverNeeded = _rolloverNeeded
          this.serverYear = data.year
          this.serverDateFormatted = `${data.day} ${this.monthNames[data.month - 1]} ${data.year}`
          this.showRolloverReminder = _rolloverNeeded
        }
      } catch (err) {
        console.error('Failed to fetch server date:', err)
      }
    },
    computeTableHeight () {
      if (!this.$refs.calWrap) return
      this.tableHeight = 'auto'
      this.$nextTick(() => {
        const wrap = this.$refs.calWrap
        if (!wrap) return
        const h = wrap.scrollHeight
        this.tableHeight = `${h < 600 ? h + 15 : 600}px`
        this.computeSpacerWidth()
      })
    },
    toggleMetaCol (key) {
      const idx = this.hiddenMetaCols.indexOf(key)
      if (idx === -1) this.hiddenMetaCols.push(key)
      else this.hiddenMetaCols.splice(idx, 1)
      this.$nextTick(this.computeSpacerWidth)
      this.saveColSettings()
    },
    computeSpacerWidth () {
      const wrap = this.$refs.calWrap
      if (!wrap) return
      // Measure sticky columns (#, Client) from DOM
      const numEl = wrap.querySelector('.tp-cal-th-num')
      const clientEl = wrap.querySelector('.tp-cal-th-company')
      const numW = numEl ? numEl.offsetWidth : 50
      const clientW = clientEl ? clientEl.offsetWidth : 200
      // Measure last month (Dec) and actions column from DOM
      const monthThs = wrap.querySelectorAll('.tp-cal-th-month')
      const decEl = monthThs.length ? monthThs[monthThs.length - 1] : null
      const actionsEl = wrap.querySelector('.tp-cal-td-actions')
      const decW = decEl ? decEl.offsetWidth : 90
      const actionsW = actionsEl ? actionsEl.offsetWidth : 80
      // Spacer = remaining visible width so Dec+Actions land flush against sticky cols
      this.spacerWidth = Math.max(0, wrap.clientWidth - numW - clientW - decW - actionsW)
    },
    async fetchSlot (slot) {
      this.loading = true
      try {
        const data = await this.adminFetch(`/api/admin/tasks/tax-clients.php?slot=${slot}`)
        if (data === null) { this.pendingAdminRetry = () => this.fetchSlot(slot); return }
        if (data && data.success) {
          this.$set(this.taxCache, slot, data.data || [])
          this.$nextTick(this.computeTableHeight)
        }
      } catch (err) {
        console.error('Failed to load tax clients:', err)
      } finally {
        this.loading = false
      }
    },
    formatClientType (val) {
      const map = { sdn_bhd: 'Sdn Bhd', llp: 'LLP', sole_prop: 'Sole Prop', partnership: 'Partnership', salaried_person: 'Salaried' }
      return map[val] || val || '—'
    },
    formatYearEnd (val) {
      return this.monthNames[val - 1] || '—'
    },
    getDeadlines (client, month) {
      const tax = (client.tax || []).filter(item => item.date && item.date.month === month)
      const adhoc = (client.adhoc || []).filter(item => item.date && item.date.month === month)
      return [...tax, ...adhoc].sort((a, b) => {
        const da = (a.date && a.date.day) || Infinity
        const db = (b.date && b.date.day) || Infinity
        return da - db
      })
    },
    showStatus (dl, month) {
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current' && month <= this.currentMonth) return true
      return dl.status === 'completed'
    },
    isOverduePending (dl, month) {
      if (dl.status !== 'pending') return false
      if (this.selectedSlot === 'prev') return true
      if (this.selectedSlot === 'current') return month < this.currentMonth
      return false
    },
    isMonthEnd (month, day) {
      const lastDays = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      return day === lastDays[month]
    },
    deadlineClass (dl, month) {
      if (dl.is_adhoc) {
        if (dl.status === 'completed') return 'tp-dl--adhoc tp-dl--adhoc-done'
        const isDue = this.selectedSlot === 'prev' || (this.selectedSlot === 'current' && month <= this.currentMonth)
        return isDue ? 'tp-dl--adhoc tp-dl--adhoc-due' : 'tp-dl--adhoc'
      }
      if (dl.name === 'tax_filing') return 'tp-dl--tax-filing'
      if (dl.status === 'completed') return 'tp-dl--grey'
      if (this.selectedSlot !== 'current' || month !== this.currentMonth) return 'tp-dl--grey'
      if (dl.name === 'mitrs') return 'tp-dl--mitrs'
      if (dl.name === 'tp_reminder' || dl.name === 'transfer_pricing') return 'tp-dl--tp-reminder'
      if (dl.name.startsWith('cp204')) return 'tp-dl--cp204'
      return 'tp-dl--grey'
    },
    async updateTaskStatus (client, dl, newStatus) {
      if (dl.is_adhoc) { return this.updateAdhocStatus(client, dl, newStatus) }
      const key = `${client.id}-${dl.name}`
      this.$set(this.updatingTask, key, true)
      try {
        const data = await this.adminFetch('/api/admin/tasks/update-tax-status.php', {
          method: 'POST',
          body: JSON.stringify({ client_id: client.id, task_name: dl.name, slot: this.selectedSlot, status: newStatus })
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateTaskStatus(client, dl, newStatus); return }
        if (data && data.success) this.$set(dl, 'status', newStatus)
      } catch (err) {
        console.error('Failed to update task status:', err)
      } finally {
        this.$set(this.updatingTask, key, false)
      }
    },
    async updateAdhocStatus (client, dl, newStatus) {
      this.$set(this.updatingAdhoc, dl.id, true)
      try {
        const data = await this.adminFetch('/api/admin/tasks/update-adhoc-status.php', {
          method: 'POST',
          body: JSON.stringify({ id: dl.id, status: newStatus })
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateAdhocStatus(client, dl, newStatus); return }
        if (data && data.success) this.$set(dl, 'status', newStatus)
      } catch (err) {
        console.error('Failed to update ad hoc status:', err)
      } finally {
        this.$set(this.updatingAdhoc, dl.id, false)
      }
    },
    // ── Deadline status custom dropdown (portal) ─────────────────────────────
    openDlDropdown (client, dl, e) {
      const key = dl.is_adhoc ? 'adhoc-' + dl.id : client.id + '-' + dl.name
      // Toggle closed if same chip clicked again
      if (this.openDropdownKey === key) {
        this.closeDlDropdown()
        return
      }
      this.openDropdownKey = key
      this.openDropdownClient = client
      this.openDropdownDl = dl
      this._dlTriggerEl = e.currentTarget
      this.$nextTick(this.positionDlDropdown)
    },
    positionDlDropdown () {
      if (!this._dlTriggerEl) return
      const r = this._dlTriggerEl.getBoundingClientRect()
      this.dropdownStyle = {
        position: 'fixed',
        top: (r.bottom + 4) + 'px',
        left: r.left + 'px',
        zIndex: 10000
      }
    },
    closeDlDropdown () {
      this.openDropdownKey = null
      this.openDropdownClient = null
      this.openDropdownDl = null
      this._dlTriggerEl = null
    },
    onDlOutsideClick (e) {
      const menu = this.$refs.tpDlMenu
      if (menu && menu.contains(e.target)) return
      if (this.openDropdownKey) this.closeDlDropdown()
    },
    onDlScroll () {
      if (this.openDropdownKey) this.positionDlDropdown()
    },
    async selectDlStatus (status) {
      if (!this.openDropdownClient || !this.openDropdownDl) return
      const client = this.openDropdownClient
      const dl = this.openDropdownDl
      this.closeDlDropdown()
      await this.updateTaskStatus(client, dl, status)
    },
    // ─────────────────────────────────────────────────────────────────────────
    onAdHocSaved ({ clientId, item }) {
      const slotYears = { prev: this.currentYear - 1, current: this.currentYear, next: this.currentYear + 1 }
      for (const slot of ['prev', 'current', 'next']) {
        if (slotYears[slot] !== item.year) continue
        const clients = this.taxCache[slot]
        if (!clients) continue
        const client = clients.find(c => c.id === clientId)
        if (client) {
          if (!client.adhoc) this.$set(client, 'adhoc', [])
          client.adhoc.push(item)
        }
      }
    },
    confirmDeleteAdhoc (item) {
      this.adhocDeleteModal = { show: true, deleting: false, item }
    },
    async deleteAdhoc () {
      const item = this.adhocDeleteModal.item
      if (!item) return
      this.adhocDeleteModal.deleting = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/delete-adhoc-deadline.php', {
          method: 'POST',
          body: JSON.stringify({ id: item.adhocId, client_id: item.clientId })
        })
        if (data === null) { this.pendingAdminRetry = () => this.deleteAdhoc(); return }
        if (data && data.success) {
          for (const slot of ['prev', 'current', 'next']) {
            const clients = this.taxCache[slot]
            if (!clients) continue
            const client = clients.find(c => c.id === item.clientId)
            if (client && client.adhoc) {
              const idx = client.adhoc.findIndex(a => a.id === item.adhocId)
              if (idx !== -1) client.adhoc.splice(idx, 1)
            }
          }
          this.adhocDeleteModal.show = false
        }
      } catch (err) {
        console.error('Failed to delete ad hoc deadline:', err)
      } finally {
        this.adhocDeleteModal.deleting = false
      }
    },
    async migrateYear (skipConfirm = false) {
      this.migrating = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/migrate-tax-year.php', { method: 'POST' })
        if (data === null) { this.pendingAdminRetry = () => this.migrateYear(skipConfirm); return }
        if (data && data.success) {
          _rolloverNeeded = false
          this.rolloverNeeded = false
          this.showRolloverReminder = false
          this.$set(this, 'taxCache', { prev: null, current: null, next: null })
          this.selectedSlot = 'current'
          await this.fetchSlot('current')
        }
      } catch (err) {
        console.error('Migration failed:', err)
      } finally {
        this.migrating = false
      }
    },
    // ── Admin dates ───────────────────────────────────────────────────────────
    async fetchAdminDates () {
      try {
        const data = await this.adminFetch(`/api/admin/tasks/get-admin-dates.php?context=tax&year=${this.calendarYear}`)
        if (data && data.success) this.adminDates = data.data || []
      } catch (err) {
        console.error('Failed to load admin dates:', err)
      }
    },
    onAdminDateSaved (item) {
      if (item.year === this.calendarYear) this.adminDates.push(item)
    },
    confirmDeleteAdminDate (ad) {
      this.adminDateDeleteModal = { show: true, deleting: false, item: ad }
    },
    async deleteAdminDate () {
      const item = this.adminDateDeleteModal.item
      if (!item) return
      this.adminDateDeleteModal.deleting = true
      try {
        const data = await this.adminFetch('/api/admin/tasks/delete-admin-date.php', {
          method: 'POST',
          body: JSON.stringify({ id: item.id })
        })
        if (data === null) { this.pendingAdminRetry = () => this.deleteAdminDate(); return }
        if (data && data.success) {
          const idx = this.adminDates.findIndex(a => a.id === item.id)
          if (idx !== -1) this.adminDates.splice(idx, 1)
          this.adminDateDeleteModal.show = false
        }
      } catch (err) {
        console.error('Failed to delete admin date:', err)
      } finally {
        this.adminDateDeleteModal.deleting = false
      }
    },
    async updateAdminDateStatus (ad, newStatus) {
      this.$set(this.updatingAdminDate, ad.id, true)
      try {
        const data = await this.adminFetch('/api/admin/tasks/update-admin-date-status.php', {
          method: 'POST',
          body: JSON.stringify({ id: ad.id, status: newStatus })
        })
        if (data === null) { this.pendingAdminRetry = () => this.updateAdminDateStatus(ad, newStatus); return }
        if (data && data.success) {
          const item = this.adminDates.find(a => a.id === ad.id)
          if (item) this.$set(item, 'status', newStatus)
        }
      } catch (err) {
        console.error('Failed to update admin date status:', err)
      } finally {
        this.$set(this.updatingAdminDate, ad.id, false)
      }
    }
    // ─────────────────────────────────────────────────────────────────────────
  }
}
</script>
